import { useState, useCallback, useMemo } from 'react';
import { Link, router } from '@inertiajs/react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, Button } from '../../../components/ui';
import Input, { InputLabel, InputGroup } from '../../../components/ui/Input';
import { cn } from '../../../lib/cn';

// Average adult reading speed for study material
// TODO: Track actual student reading speed/level and use personalized estimates
const WORDS_PER_MINUTE = 200;

function estimateReadingTime(wordCount) {
  if (!wordCount || wordCount === 0) return null;
  const minutes = Math.ceil(wordCount / WORDS_PER_MINUTE);
  if (minutes < 1) return '< 1 min';
  return `${minutes} min`;
}

export default function UnitPlan({ course, unit, book, chapters, existingLessons }) {
  const [activeChapter, setActiveChapter] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [preReading, setPreReading] = useState(unit.pre_reading || false);

  // Position lookup for sorting chapters by book order
  const chapterPositionMap = useMemo(
    () => new Map(chapters.map(ch => [ch.id, ch.position])),
    [chapters]
  );

  const sortByBookOrder = useCallback((ids) => {
    return [...ids].sort((a, b) => (chapterPositionMap.get(a) ?? 0) - (chapterPositionMap.get(b) ?? 0));
  }, [chapterPositionMap]);

  const [lessonSlots, setLessonSlots] = useState(() => {
    if (existingLessons && existingLessons.length > 0) {
      return existingLessons.map((l, i) => ({
        id: `slot-${l.id || i}`,
        lessonId: l.id,
        title: l.title,
        chapterIds: l.chapter_ids || [],
      }));
    }
    return [{ id: 'slot-0', title: 'Lesson 1', chapterIds: [] }];
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  // All chapter IDs currently assigned to any slot
  const assignedChapterIds = useMemo(
    () => lessonSlots.flatMap(s => s.chapterIds),
    [lessonSlots]
  );

  const assignedCount = assignedChapterIds.length;

  const totalWords = useMemo(
    () => chapters.reduce((sum, ch) => sum + (ch.word_count || 0), 0),
    [chapters]
  );

  const assignedWords = useMemo(
    () => assignedChapterIds.reduce((sum, id) => {
      const ch = chapters.find(c => c.id === id);
      return sum + (ch?.word_count || 0);
    }, 0),
    [assignedChapterIds, chapters]
  );

  // ── DnD handlers ──

  const handleDragStart = useCallback((event) => {
    const data = event.active.data.current;
    if (data?.type === 'chapter' || data?.type === 'assigned-chapter') {
      setActiveChapter(chapters.find(ch => ch.id === data.chapterId) || null);
    }
  }, [chapters]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    setActiveChapter(null);
    if (!over) return;

    const activeData = active.data.current;
    const overId = String(over.id);

    // Target must be a droppable lesson slot
    const targetSlot = lessonSlots.find(s => s.id === overId);
    if (!targetSlot) return;

    if (activeData?.type === 'chapter') {
      // Unassigned chapter → slot
      const chapterId = activeData.chapterId;
      if (targetSlot.chapterIds.includes(chapterId)) return;

      setLessonSlots(prev => prev.map(slot => {
        if (slot.id === targetSlot.id) {
          return { ...slot, chapterIds: sortByBookOrder([...slot.chapterIds, chapterId]) };
        }
        return { ...slot, chapterIds: slot.chapterIds.filter(id => id !== chapterId) };
      }));
      return;
    }

    if (activeData?.type === 'assigned-chapter') {
      // Assigned chapter → different slot
      const chapterId = activeData.chapterId;
      const sourceSlotId = activeData.sourceSlotId;

      if (sourceSlotId === targetSlot.id) return;
      if (targetSlot.chapterIds.includes(chapterId)) return;

      setLessonSlots(prev => prev.map(slot => {
        if (slot.id === sourceSlotId) {
          return { ...slot, chapterIds: slot.chapterIds.filter(id => id !== chapterId) };
        }
        if (slot.id === targetSlot.id) {
          return { ...slot, chapterIds: sortByBookOrder([...slot.chapterIds, chapterId]) };
        }
        return slot;
      }));
    }
  }, [lessonSlots, sortByBookOrder]);

  // ── Lesson slot management ──

  const setLessonCount = useCallback((newCount) => {
    setLessonSlots(prev => {
      if (newCount > prev.length) {
        const added = Array.from({ length: newCount - prev.length }, (_, i) => ({
          id: `slot-${Date.now()}-${i}`,
          title: `Lesson ${prev.length + i + 1}`,
          chapterIds: [],
        }));
        return [...prev, ...added];
      }
      if (newCount < prev.length) {
        return prev.slice(0, newCount);
      }
      return prev;
    });
  }, []);

  const updateSlotTitle = useCallback((index, title) => {
    setLessonSlots(prev => prev.map((slot, i) =>
      i === index ? { ...slot, title } : slot
    ));
  }, []);

  const removeChapterFromSlot = useCallback((slotIndex, chapterId) => {
    setLessonSlots(prev => prev.map((slot, i) =>
      i === slotIndex
        ? { ...slot, chapterIds: slot.chapterIds.filter(id => id !== chapterId) }
        : slot
    ));
  }, []);

  const removeLesson = useCallback((index) => {
    const slot = lessonSlots[index];
    const hasContent = slot.chapterIds.length > 0;
    const msg = hasContent
      ? `Delete "${slot.title}"? This lesson has ${slot.chapterIds.length} chapter(s) assigned. This cannot be undone.`
      : `Delete "${slot.title}"?`;
    if (!confirm(msg)) return;

    setLessonSlots(prev => prev.filter((_, i) => i !== index));

    // Delete from server if it's a persisted lesson
    if (slot.lessonId) {
      router.delete(`/teacher/courses/${course.id}/units/${unit.id}/lessons/${slot.lessonId}`, {
        preserveState: true,
        preserveScroll: true,
      });
    }
  }, [lessonSlots, course.id, unit.id]);

  const autoDistribute = useCallback(() => {
    const chapterIds = chapters.map(ch => ch.id);
    const count = lessonSlots.length;
    const chunkSize = Math.ceil(chapterIds.length / count);

    setLessonSlots(prev => prev.map((slot, i) => ({
      ...slot,
      chapterIds: chapterIds.slice(i * chunkSize, (i + 1) * chunkSize),
    })));
  }, [chapters, lessonSlots.length]);

  // ── Save ──

  const handleSave = () => {
    setIsSaving(true);
    router.post(
      `/teacher/courses/${course.id}/units/${unit.id}/save_plan`,
      {
        lessons: lessonSlots.map((slot, i) => ({
          title: slot.title || `Lesson ${i + 1}`,
          chapter_ids: slot.chapterIds,
        })),
        pre_reading: preReading,
      },
      { onFinish: () => setIsSaving(false) }
    );
  };

  const saveBar = (
    <div className="py-3 flex items-center justify-between max-w-7xl mx-auto w-full">
      <div className="text-sm text-gray-500 dark:text-gray-400">
        {lessonSlots.length} lesson{lessonSlots.length !== 1 ? 's' : ''} &middot;{' '}
        {assignedCount} of {chapters.length} chapters assigned
        {assignedWords > 0 && (
          <> &middot; ~{estimateReadingTime(assignedWords)} reading</>
        )}
      </div>
      <div className="flex items-center gap-3">
        <Link href={`/teacher/courses/${course.id}`}>
          <Button variant="ghost">Cancel</Button>
        </Link>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isSaving || assignedCount === 0}
        >
          {isSaving ? 'Saving...' : 'Save Plan'}
        </Button>
      </div>
    </div>
  );

  return (
    <TeacherShell scrollTitle={<><Link href={`/teacher/courses/${course.id}`} className="hover:text-blue-600 dark:hover:text-blue-400">{course.title}</Link>{'  ›  '}{unit.title}{'  ›  Plan'}</>} footer={saveBar}>
      <TeacherPageHeader
        title="Plan Chapters"
        subtitle={
          <>
            <a
              href={`/teacher/library/${book.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
              {book.title}
            </a>
            {book.author && <span className="text-gray-400 dark:text-gray-500"> by {book.author}</span>}
          </>
        }
        backLink={`/teacher/courses/${course.id}`}
        backLabel="Back to course"
      />

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
        <InputGroup className="flex items-center gap-2">
          <InputLabel className="text-sm whitespace-nowrap">Lessons:</InputLabel>
          <Input
            type="number"
            min={1}
            max={chapters.length}
            value={lessonSlots.length}
            onChange={(e) => setLessonCount(Math.max(1, parseInt(e.target.value) || 1))}
            className="w-20 text-center"
          />
        </InputGroup>
        <Button variant="secondary" size="sm" onClick={autoDistribute}>
          Auto-distribute
        </Button>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={preReading}
            onChange={(e) => setPreReading(e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-600 dark:text-gray-400">Reading before first lesson</span>
        </label>
        <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
          {chapters.length} chapters &middot; {totalWords.toLocaleString()} words
          {estimateReadingTime(totalWords) && <> &middot; ~{estimateReadingTime(totalWords)} total</>}
        </span>
      </div>

      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Unassigned chapters */}
          <div>
            <ChapterSourcePanel
              chapters={chapters}
              assignedChapterIds={assignedChapterIds}
            />
          </div>

          {/* Right: Lesson slots */}
          <div className="lg:col-span-2 space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Lessons ({lessonSlots.length})
            </h3>
            {lessonSlots.map((slot, index) => (
              <LessonSlot
                key={slot.id}
                slot={slot}
                index={index}
                chapters={chapters}
                onUpdateTitle={updateSlotTitle}
                onRemoveChapter={removeChapterFromSlot}
                onRemoveLesson={removeLesson}
              />
            ))}
          </div>
        </div>

        <DragOverlay>
          {activeChapter ? <ChapterDragOverlay chapter={activeChapter} /> : null}
        </DragOverlay>
      </DndContext>
    </TeacherShell>
  );
}

/* ── Chapter Source Panel ── */

function ChapterSourcePanel({ chapters, assignedChapterIds }) {
  const unassigned = chapters.filter(ch => !assignedChapterIds.includes(ch.id));

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
        Unassigned Chapters ({unassigned.length})
      </h3>
      {unassigned.length === 0 ? (
        <div className="p-4 text-center text-sm text-gray-400 dark:text-gray-500 border border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
          All chapters assigned
        </div>
      ) : (
        <div className="space-y-1 max-h-[calc(100vh-280px)] overflow-y-auto">
          {unassigned.map(ch => (
            <UnassignedChapterItem key={ch.id} chapter={ch} />
          ))}
        </div>
      )}
    </div>
  );
}

function UnassignedChapterItem({ chapter }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `chapter-${chapter.id}`,
    data: { type: 'chapter', chapterId: chapter.id },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={cn(
        'p-3 rounded-lg border cursor-grab active:cursor-grabbing touch-none',
        'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700',
        'hover:border-gray-300 dark:hover:border-gray-600 transition-colors',
        isDragging && 'opacity-30'
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {chapter.formatted_label || chapter.title}
          </span>
        </div>
        {chapter.word_count > 0 && (
          <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 ml-2">
            {chapter.word_count.toLocaleString()} words
          </span>
        )}
      </div>
    </div>
  );
}

function ChapterDragOverlay({ chapter }) {
  return (
    <div className="p-3 rounded-lg border-2 border-blue-500 dark:border-blue-400 bg-white dark:bg-gray-800 shadow-2xl opacity-90 pointer-events-none max-w-xs">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{chapter.formatted_label || chapter.title}</span>
      </div>
    </div>
  );
}

/* ── Lesson Slot ── */

function LessonSlot({ slot, index, chapters, onUpdateTitle, onRemoveChapter, onRemoveLesson }) {
  const { isOver, setNodeRef } = useDroppable({
    id: slot.id,
    data: { type: 'lesson-slot', slotIndex: index },
  });

  const slotChapters = slot.chapterIds
    .map(id => chapters.find(ch => ch.id === id))
    .filter(Boolean);

  const totalWords = slotChapters.reduce((sum, ch) => sum + (ch.word_count || 0), 0);
  const readingTime = estimateReadingTime(totalWords);

  return (
    <Card
      ref={setNodeRef}
      className={cn(
        'p-4 transition-all',
        isOver && 'ring-2 ring-blue-500 dark:ring-blue-400 bg-blue-50/50 dark:bg-blue-900/10'
      )}
    >
      <div className="flex items-center gap-3 mb-3">
        <span className="text-xs font-mono text-gray-400 w-6 text-center">{index + 1}</span>
        <Input
          value={slot.title}
          onChange={(e) => onUpdateTitle(index, e.target.value)}
          className="text-sm font-medium flex-1"
          placeholder={`Lesson ${index + 1}`}
        />
        <button
          onClick={() => onRemoveLesson(index)}
          className="p-1.5 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0"
          title="Delete lesson"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>

      {slotChapters.length === 0 ? (
        <div className={cn(
          'p-4 rounded border-2 border-dashed text-center text-sm',
          isOver
            ? 'border-blue-400 text-blue-600 dark:border-blue-500 dark:text-blue-400'
            : 'border-gray-200 text-gray-400 dark:border-gray-700 dark:text-gray-500'
        )}>
          Drop chapters here
        </div>
      ) : (
        <div className="space-y-1">
          {slotChapters.map(ch => (
            <AssignedChapterItem
              key={ch.id}
              chapter={ch}
              slotId={slot.id}
              onRemove={() => onRemoveChapter(index, ch.id)}
            />
          ))}
        </div>
      )}

      <div className="mt-2 text-xs text-gray-400 dark:text-gray-500">
        {slotChapters.length} chapter{slotChapters.length !== 1 ? 's' : ''}
        {totalWords > 0 && <> &middot; {totalWords.toLocaleString()} words</>}
        {readingTime && <> &middot; ~{readingTime} reading</>}
      </div>
    </Card>
  );
}

function AssignedChapterItem({ chapter, slotId, onRemove }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `assigned-${slotId}-${chapter.id}`,
    data: { type: 'assigned-chapter', chapterId: chapter.id, sourceSlotId: slotId },
  });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex items-center gap-2 p-2 rounded border',
        'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
        isDragging && 'opacity-30'
      )}
    >
      <button
        {...attributes}
        {...listeners}
        className="p-0.5 cursor-grab active:cursor-grabbing touch-none text-gray-400"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
        </svg>
      </button>
      <span className="text-sm text-gray-900 dark:text-white truncate flex-1">{chapter.formatted_label || chapter.title}</span>
      <button
        onClick={onRemove}
        className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
